/**
 * DayTimetable (N4b) — one reusable day grid, used full-size on the Follow-Ups
 * page (right third) and in a `compact` variant on the Dashboard.
 *
 * Model: NAIVE wall-clock, no timezone conversion (matches N2b + the N4a list).
 * An item sits at its literal clock position for every viewer. When the grid
 * shows SOMEONE ELSE's day in a different zone we don't convert — we LABEL it
 * ("Jacob's day · Europe/Warsaw"), and label an item's assignee chip when that
 * assignee's zone differs from the viewer's. The "now" line follows the VIEWER's
 * real time (so it may sit oddly on another person's grid — the label explains why).
 *
 * Fixed window 7am–7pm in wall-clock. Items outside it collapse to "N earlier" /
 * "N later" indicators. Untimed follow-ups/reminders never sit on the grid — they
 * live in an "Untimed today" strip above it. Overlapping items lay out side-by-side.
 *
 * Reminders (Item 2) are the viewer's personal layer (own + others' shared),
 * visually distinct, created/edited inline (no modal). Drag-to-reschedule (Item 3)
 * uses native HTML5 drag, snaps to 15 min, is optimistic with snap-back + inline
 * error, and writes through the existing updateFollowUp path (via onReschedule).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CRM, tintOf } from '../lib/theme';
import { useApiClient } from '../hooks/useApi';
import { clockInTz, todayInTz, tzCityLabel, shortTime } from '../lib/time';
import { formatJobNo } from '../lib/jobNumber';
import { Icons } from '../lib/icons';

const C = { ...CRM, violet: CRM.violet, mono: "'DM Mono', monospace" };

const WIN_START = 7 * 60;   // 7:00am
const WIN_END = 19 * 60;    // 7:00pm
const WIN_MIN = WIN_END - WIN_START;

function timeToMin(t: string | null | undefined): number | null {
  if (!t) return null;
  const m = String(t).match(/^(\d{1,2}):(\d{2})/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}
function minToTime(min: number): string {
  const h = Math.floor(min / 60), m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
}
function hourLabel(h: number): string {
  const ampm = h < 12 ? 'am' : 'pm';
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}${ampm}`;
}

interface Viewer { id?: string; role?: string; timezone?: string | null }

interface DayTimetableProps {
  date: string;                    // 'YYYY-MM-DD' the grid renders
  followUps: any[];                // open follow-ups (any date — filtered to `date` here)
  users: any[];
  leadById: Map<string, any>;
  viewer: Viewer | null;
  tz: string;                      // viewer's tz (now-line + labels)
  gridOwnerName?: string;          // set when viewing another user's list
  gridTz?: string;                 // that user's tz (for the "whose day" label)
  compact?: boolean;
  editable?: boolean;              // parent asserts: date >= today (drag allowed)
  onSelectLead?: (leadId: string) => void;
  onReschedule?: (fuId: string, patch: { follow_up_date: string; follow_up_time: string | null }) => Promise<void>;
  onContentChange?: (hasContent: boolean) => void; // so a host can hide an empty grid
}

// A positioned block on the grid.
interface Block {
  key: string; kind: 'fu' | 'rem'; ref: any;
  top: number; bottom: number; lane: number; cols: number;
}

export function DayTimetable({
  date, followUps, users, leadById, viewer, tz,
  gridOwnerName, gridTz, compact = false, editable = false,
  onSelectLead, onReschedule, onContentChange,
}: DayTimetableProps) {
  const client = useApiClient();
  const hourH = compact ? 34 : 46;
  const gridH = 12 * hourH;
  const ITEM_H = compact ? 26 : 34;

  // Drag-to-reschedule is enabled whenever the parent says the date is today/future
  // — in BOTH variants (compact included). Past days are read-only. Reminder
  // CREATION is gated separately to the full variant (see the header button).
  const canEdit = editable;

  // ---- reminders (self-managed: viewer's own + others' shared, for `date`) ----
  const [reminders, setReminders] = useState<any[]>([]);
  const loadReminders = useCallback(async () => {
    try {
      const r = await client.getReminders(date);
      setReminders(r.data || []);
    } catch {
      /* reminders are a soft layer — a load failure shouldn't blank the grid */
    }
  }, [client, date]);
  useEffect(() => { loadReminders(); }, [loadReminders]);

  // ---- optimistic overrides for drag-to-reschedule ----
  const [fuOverride, setFuOverride] = useState<Record<string, { date: string; time: string | null }>>({});
  const [remOverride, setRemOverride] = useState<Record<string, { date: string; time: string | null }>>({});
  const [dragError, setDragError] = useState('');
  const [dropHint, setDropHint] = useState<number | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // ---- live "now" line (viewer's real time, this zone) ----
  const computeNow = useCallback(() => {
    if (date !== todayInTz(tz)) return null;
    return timeToMin(clockInTz(tz).time);
  }, [date, tz]);
  const [nowMin, setNowMin] = useState<number | null>(computeNow);
  useEffect(() => {
    setNowMin(computeNow());
    const id = window.setInterval(() => setNowMin(computeNow()), 30000);
    return () => window.clearInterval(id);
  }, [computeNow]);

  const isAdmin = viewer?.role === 'admin';
  const userTzById = useMemo(() => new Map(users.map((u) => [u.id, u.timezone])), [users]);
  const userNameById = (id: string) => {
    const u = users.find((x) => x.id === id);
    return u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email : '';
  };

  // Apply optimistic overrides, then split each layer into on-grid / off / untimed.
  const dayFus = useMemo(
    () => followUps.filter((f) => (fuOverride[f.id]?.date ?? f.follow_up_date) === date)
      .map((f) => fuOverride[f.id] ? { ...f, follow_up_date: fuOverride[f.id].date, follow_up_time: fuOverride[f.id].time } : f),
    [followUps, fuOverride, date]
  );
  const dayRems = useMemo(
    () => reminders.filter((r) => (remOverride[r.id]?.date ?? r.reminder_date) === date)
      .map((r) => remOverride[r.id] ? { ...r, reminder_date: remOverride[r.id].date, reminder_time: remOverride[r.id].time } : r),
    [reminders, remOverride, date]
  );

  const { blocks, earlier, later, untimed } = useMemo(() => {
    const raw: { kind: 'fu' | 'rem'; ref: any; min: number | null }[] = [
      ...dayFus.map((f) => ({ kind: 'fu' as const, ref: f, min: timeToMin(f.follow_up_time) })),
      ...dayRems.map((r) => ({ kind: 'rem' as const, ref: r, min: timeToMin(r.reminder_time) })),
    ];
    const untimed = raw.filter((i) => i.min == null);
    const earlier = raw.filter((i) => i.min != null && (i.min as number) < WIN_START);
    const later = raw.filter((i) => i.min != null && (i.min as number) > WIN_END);
    const onGrid = raw.filter((i) => i.min != null && (i.min as number) >= WIN_START && (i.min as number) <= WIN_END);

    // Lane assignment (calendar side-by-side): sort by start, place in first free lane.
    const items: Block[] = onGrid
      .map((i) => {
        const top = ((i.min as number) - WIN_START) / 60 * hourH;
        return { key: `${i.kind}-${i.ref.id}`, kind: i.kind, ref: i.ref, top, bottom: top + ITEM_H, lane: 0, cols: 1 };
      })
      .sort((a, b) => a.top - b.top || a.bottom - b.bottom);
    const laneEnds: number[] = [];
    items.forEach((it) => {
      let placed = false;
      for (let li = 0; li < laneEnds.length; li++) {
        if (laneEnds[li] <= it.top) { it.lane = li; laneEnds[li] = it.bottom; placed = true; break; }
      }
      if (!placed) { it.lane = laneEnds.length; laneEnds.push(it.bottom); }
    });
    // Column count per item = widest overlap it participates in (keeps left+width ≤ 100%).
    items.forEach((it) => {
      const overlap = items.filter((o) => o.top < it.bottom && o.bottom > it.top);
      it.cols = Math.max(...overlap.map((o) => o.lane)) + 1;
    });
    return { blocks: items, earlier, later, untimed };
  }, [dayFus, dayRems, hourH]);

  // Report whether the day has anything on it (a lone reminder counts) so a host
  // like the dashboard can hide an empty grid without duplicating the reminder fetch.
  const hasContent = blocks.length > 0 || untimed.length > 0 || earlier.length > 0 || later.length > 0;
  useEffect(() => { onContentChange?.(hasContent); }, [hasContent, onContentChange]);

  // ---------------- drag / drop ----------------
  const canDragItem = (kind: 'fu' | 'rem', ref: any) => {
    if (!canEdit) return false;
    if (kind === 'rem') return ref.user_id === viewer?.id || isAdmin;
    return true; // any company user may reschedule a follow-up (shared files, N3)
  };

  const onItemDragStart = (e: React.DragEvent, kind: 'fu' | 'rem', ref: any) => {
    if (!canDragItem(kind, ref)) { e.preventDefault(); return; }
    e.dataTransfer.setData('text/plain', JSON.stringify({ kind, id: ref.id }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const minFromEvent = (e: React.DragEvent): number => {
    const rect = gridRef.current!.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const raw = WIN_START + (y / hourH) * 60;
    const snapped = Math.round(raw / 15) * 15;
    return Math.max(WIN_START, Math.min(WIN_END, snapped));
  };

  const onGridDragOver = (e: React.DragEvent) => {
    if (!canEdit) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropHint(minFromEvent(e));
  };
  const onGridDragLeave = () => setDropHint(null);

  const onGridDrop = async (e: React.DragEvent) => {
    if (!canEdit) return;
    e.preventDefault();
    setDropHint(null);
    let payload: { kind: 'fu' | 'rem'; id: string };
    try { payload = JSON.parse(e.dataTransfer.getData('text/plain')); } catch { return; }
    const newTime = minToTime(minFromEvent(e));
    if (payload.kind === 'fu') await moveFollowUp(payload.id, newTime);
    else await moveReminder(payload.id, newTime);
  };

  async function moveFollowUp(id: string, newTime: string) {
    if (!onReschedule) return;
    const fu = followUps.find((f) => f.id === id);
    if (!fu) return;
    // No-op if it's already at that exact slot.
    if (fu.follow_up_date === date && shortTime(fu.follow_up_time) === shortTime(newTime)) return;
    setDragError('');
    setFuOverride((o) => ({ ...o, [id]: { date, time: newTime } }));
    try {
      await onReschedule(id, { follow_up_date: date, follow_up_time: newTime });
      setFuOverride((o) => { const n = { ...o }; delete n[id]; return n; });
    } catch (err: any) {
      setFuOverride((o) => { const n = { ...o }; delete n[id]; return n; }); // snap back
      const name = leadNameOf(fu) || 'this follow-up';
      setDragError(`Couldn't move ${name}: ${err?.message || 'network error'}. It stayed put.`);
    }
  }

  async function moveReminder(id: string, newTime: string) {
    const rem = reminders.find((r) => r.id === id);
    if (!rem) return;
    if (rem.reminder_date === date && shortTime(rem.reminder_time) === shortTime(newTime)) return;
    setDragError('');
    setRemOverride((o) => ({ ...o, [id]: { date, time: newTime } }));
    try {
      await client.updateReminder(id, { reminder_date: date, reminder_time: newTime });
      await loadReminders();
      setRemOverride((o) => { const n = { ...o }; delete n[id]; return n; });
    } catch (err: any) {
      setRemOverride((o) => { const n = { ...o }; delete n[id]; return n; });
      setDragError(`Couldn't move reminder "${rem.title}": ${err?.message || 'network error'}. It stayed put.`);
    }
  }

  const leadNameOf = (fu: any) => {
    const l = leadById.get(fu.lead_id);
    return l ? `${l.first_name ?? ''} ${l.last_name ?? ''}`.trim() : '';
  };

  // ---------------- reminder inline form ----------------
  const emptyForm = { title: '', time: '', notes: '', shared: false };
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState('');

  const openCreate = () => { setEditId(null); setForm(emptyForm); setFormErr(''); setFormOpen(true); };
  const openEdit = (rem: any) => {
    setEditId(rem.id);
    setForm({ title: rem.title || '', time: shortTime(rem.reminder_time), notes: rem.notes || '', shared: !!rem.shared });
    setFormErr(''); setFormOpen(true);
  };
  const closeForm = () => { setFormOpen(false); setEditId(null); setForm(emptyForm); setFormErr(''); };

  const saveForm = async () => {
    if (!form.title.trim()) { setFormErr('A title is required.'); return; }
    setSaving(true); setFormErr('');
    const body = {
      reminder_date: date,
      reminder_time: form.time ? `${form.time}:00` : null,
      title: form.title.trim(),
      notes: form.notes.trim() || null,
      shared: form.shared,
    };
    try {
      if (editId) await client.updateReminder(editId, body);
      else await client.createReminder(body);
      await loadReminders();
      closeForm();
    } catch (err: any) {
      setFormErr(err?.message || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  const deleteForm = async () => {
    if (!editId) return;
    setSaving(true); setFormErr('');
    try {
      await client.deleteReminder(editId);
      await loadReminders();
      closeForm();
    } catch (err: any) {
      setFormErr(err?.message || 'Could not delete. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const canEditReminder = (rem: any) => canEdit && (rem.user_id === viewer?.id || isAdmin);
  const showGridTz = gridTz && gridTz !== tz;

  // ---------------- render ----------------
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '6px 8px', background: C.panel, border: `1px solid ${C.border}`,
    borderRadius: '6px', color: C.text, fontSize: '12px', boxSizing: 'border-box',
  };

  const renderBlock = (b: Block) => {
    const leftPct = (b.lane / b.cols) * 100;
    const widthPct = (1 / b.cols) * 100;
    const draggable = canDragItem(b.kind, b.ref);
    const common: React.CSSProperties = {
      position: 'absolute', top: b.top, left: `calc(${leftPct}% + 2px)`, width: `calc(${widthPct}% - 4px)`,
      height: ITEM_H, borderRadius: '6px', padding: compact ? '2px 5px' : '3px 7px', overflow: 'hidden',
      cursor: draggable ? 'grab' : (onSelectLead ? 'pointer' : 'default'), boxSizing: 'border-box',
      fontSize: compact ? '10px' : '11px', lineHeight: 1.15,
    };
    if (b.kind === 'fu') {
      const fu = b.ref;
      const mine = fu.assigned_to_user_id === viewer?.id;
      const aTz = userTzById.get(fu.assigned_to_user_id);
      return (
        <div
          key={b.key} draggable={draggable}
          onDragStart={(e) => onItemDragStart(e, 'fu', fu)}
          onClick={() => onSelectLead?.(fu.lead_id)}
          title={`${leadNameOf(fu) || 'Lead'}${fu.description ? ' — ' + fu.description : ''}`}
          style={{ ...common, background: 'var(--crm-panel-2)', border: `1px solid ${C.amberSoftLine}`, borderLeft: `3px solid ${C.amberLine}` }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}>
            <span style={{ fontFamily: C.mono, color: C.amber, fontWeight: 700 }}>{shortTime(fu.follow_up_time)}</span>
            <span style={{ color: C.text, overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatJobNo(leadById.get(fu.lead_id))} {leadNameOf(fu) || 'Lead'}</span>
          </div>
          {!mine && !compact && (
            <div style={{ fontSize: '9px', color: C.accent, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {userNameById(fu.assigned_to_user_id)}{aTz && aTz !== tz ? ` · ${tzCityLabel(aTz)}` : ''}
            </div>
          )}
        </div>
      );
    }
    // reminder
    const rem = b.ref;
    const editable2 = canEditReminder(rem);
    return (
      <div
        key={b.key} draggable={draggable}
        onDragStart={(e) => onItemDragStart(e, 'rem', rem)}
        onClick={() => { if (editable2) openEdit(rem); }}
        title={`${rem.title}${rem.notes ? ' — ' + rem.notes : ''}`}
        style={{ ...common, background: 'var(--crm-violet-tint)', border: `1px dashed ${C.violetTint}`, borderLeft: `3px solid ${C.violet}`, cursor: editable2 ? (draggable ? 'grab' : 'pointer') : 'default' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}>
          <span style={{ color: C.violet, display: 'inline-flex' }}><Icons.bellOn size={compact ? 11 : 12} /></span>
          <span style={{ fontFamily: C.mono, color: C.violet, fontWeight: 700 }}>{shortTime(rem.reminder_time)}</span>
          <span style={{ color: C.text, overflow: 'hidden', textOverflow: 'ellipsis' }}>{rem.title}</span>
        </div>
        {rem.shared && !compact && (
          <div style={{ fontSize: '9px', color: C.muted }}>
            {rem.user_id === viewer?.id ? 'Shared' : `Shared · ${userNameById(rem.user_id)}`}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '12px', padding: compact ? '10px 12px' : '12px 14px' }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <span style={{ color: C.muted, display: 'inline-flex' }}><Icons.clock size={15} /></span>
        <div style={{ fontSize: '12px', fontWeight: 700, color: C.text }}>
          {gridOwnerName ? `${gridOwnerName}'s day` : 'Day'}
          {showGridTz && <span style={{ color: C.muted, fontWeight: 500 }}> · {gridTz}</span>}
        </div>
        <div style={{ flex: 1 }} />
        {canEdit && !compact && (
          <button onClick={formOpen ? closeForm : openCreate} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 9px', background: C.panel2, border: `1px solid ${C.violetTint}`, color: C.violet, borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}>
            <Icons.bellOn size={13} /> Reminder
          </button>
        )}
      </div>

      {/* inline reminder form (create/edit) — NOT a modal */}
      {formOpen && canEdit && (
        <div style={{ background: C.panel2, border: `1px solid ${C.violetTint}`, borderRadius: '9px', padding: '10px', marginBottom: '10px', display: 'flex', flexDirection: 'column', gap: '7px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: C.violet }}>{editId ? 'Edit reminder' : 'New reminder'}</div>
          <input autoFocus value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Title" style={inputStyle} aria-label="Reminder title" />
          <div style={{ display: 'flex', gap: '7px' }}>
            <input type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} style={{ ...inputStyle, colorScheme: 'dark', flex: '0 0 120px' }} aria-label="Reminder time" />
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: C.muted, cursor: 'pointer', flex: 1 }}>
              <button type="button" onClick={() => setForm((f) => ({ ...f, shared: !f.shared }))} style={{ background: 'none', border: 'none', padding: 0, color: form.shared ? C.violet : C.dim, cursor: 'pointer', display: 'inline-flex' }} aria-label={form.shared ? 'Shared with company' : 'Private'}>
                {form.shared ? <Icons.checkboxOn size={16} /> : <Icons.checkboxOff size={16} />}
              </button>
              {form.shared ? 'Shared with company' : 'Private to me'}
            </label>
          </div>
          <input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Notes (optional)" style={inputStyle} aria-label="Reminder notes" />
          {formErr && <div style={{ fontSize: '11px', color: C.red }}>{formErr}</div>}
          <div style={{ display: 'flex', gap: '7px', alignItems: 'center' }}>
            <button onClick={saveForm} disabled={saving} style={{ padding: '6px 12px', background: C.violet, border: 'none', color: 'var(--crm-on-fill)', borderRadius: '6px', cursor: saving ? 'default' : 'pointer', fontSize: '11px', fontWeight: 600, opacity: saving ? 0.6 : 1 }}>{editId ? 'Save' : 'Add'}</button>
            <button onClick={closeForm} disabled={saving} style={{ padding: '6px 12px', background: 'none', border: `1px solid ${C.border}`, color: C.muted, borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
            <div style={{ flex: 1 }} />
            {editId && (
              <button onClick={deleteForm} disabled={saving} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '6px 10px', background: 'none', border: `1px solid ${C.redSoftLine}`, color: C.red, borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}>
                <Icons.delete size={13} /> Delete
              </button>
            )}
          </div>
        </div>
      )}

      {/* drag error (inline — never an alert) */}
      {dragError && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: `${C.redTint}`, border: `1px solid ${C.redSoftLine}`, color: C.red, borderRadius: '7px', padding: '6px 9px', marginBottom: '8px', fontSize: '11px' }}>
          <span style={{ flex: 1 }}>{dragError}</span>
          <button onClick={() => setDragError('')} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', display: 'inline-flex' }} aria-label="Dismiss"><Icons.close size={13} /></button>
        </div>
      )}

      {/* earlier indicator */}
      {earlier.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', color: C.muted, marginBottom: '4px' }}><Icons.chevronUp size={12} /> {earlier.length} earlier (before 7am)</div>
      )}

      {/* untimed strip — NOT on the grid */}
      {untimed.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', alignItems: 'center', marginBottom: '8px' }}>
          <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '.4px', color: C.muted, fontWeight: 700 }}>Untimed today · {untimed.length}</span>
          {untimed.map((i) => {
            const isRem = i.kind === 'rem';
            const label = isRem ? i.ref.title : (leadNameOf(i.ref) || 'Lead');
            const drag = canDragItem(i.kind, i.ref);
            return (
              <span
                key={`${i.kind}-${i.ref.id}`}
                draggable={drag}
                onDragStart={(e) => onItemDragStart(e, i.kind, i.ref)}
                onClick={() => { if (isRem) { if (canEditReminder(i.ref)) openEdit(i.ref); } else onSelectLead?.(i.ref.lead_id); }}
                title={label}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: isRem ? C.violet : C.text, background: C.panel2, border: `1px ${isRem ? 'dashed' : 'solid'} ${isRem ? tintOf(C.violet, 40) : C.border}`, borderRadius: '6px', padding: '2px 7px', cursor: drag ? 'grab' : 'pointer', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {isRem && <Icons.bellOn size={11} />}{label}
              </span>
            );
          })}
        </div>
      )}

      {/* the grid */}
      <div
        ref={gridRef}
        onDragOver={onGridDragOver}
        onDragLeave={onGridDragLeave}
        onDrop={onGridDrop}
        style={{ position: 'relative', height: gridH, borderTop: `1px solid ${C.border}`, marginTop: '2px' }}
      >
        {/* hour rows + half-hour divisions + labels */}
        {Array.from({ length: 12 }).map((_, i) => {
          const h = 7 + i;
          const top = i * hourH;
          return (
            <React.Fragment key={h}>
              <div style={{ position: 'absolute', top, left: 0, right: 0, borderTop: `1px solid ${C.border}` }} />
              <div style={{ position: 'absolute', top: top + hourH / 2, left: 0, right: 0, borderTop: `1px dashed ${C.border}` }} />
              <div style={{ position: 'absolute', top: top + 1, left: 0, width: 34, fontSize: '9px', color: C.dim, fontFamily: C.mono }}>{hourLabel(h)}</div>
            </React.Fragment>
          );
        })}
        {/* left gutter offset for items so they clear the hour labels */}
        <div style={{ position: 'absolute', top: 0, left: 36, right: 4, bottom: 0 }}>
          {blocks.map(renderBlock)}
        </div>

        {/* drop hint line */}
        {dropHint != null && (
          <div style={{ position: 'absolute', top: (dropHint - WIN_START) / 60 * hourH, left: 34, right: 0, borderTop: `2px dashed ${C.accentLine}`, pointerEvents: 'none' }}>
            <span style={{ position: 'absolute', top: -8, left: 2, fontSize: '9px', color: C.accent, background: C.panel, padding: '0 3px', fontFamily: C.mono }}>{shortTime(minToTime(dropHint))}</span>
          </div>
        )}

        {/* now line (viewer's real time) */}
        {nowMin != null && nowMin >= WIN_START && nowMin <= WIN_END && (
          <div style={{ position: 'absolute', top: (nowMin - WIN_START) / 60 * hourH, left: 0, right: 0, borderTop: `2px solid ${C.redLine}`, pointerEvents: 'none', zIndex: 5 }}>
            <span style={{ position: 'absolute', left: 0, top: -4, width: 6, height: 6, borderRadius: '50%', background: C.redFill }} />
          </div>
        )}

        {/* empty hint — centred over the grid */}
        {blocks.length === 0 && untimed.length === 0 && earlier.length === 0 && later.length === 0 && (
          <div style={{ position: 'absolute', top: '50%', left: 36, right: 4, transform: 'translateY(-50%)', textAlign: 'center', fontSize: '11px', color: C.dim, pointerEvents: 'none' }}>Nothing scheduled.</div>
        )}
      </div>

      {/* later indicator */}
      {later.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', color: C.muted, marginTop: '4px' }}><Icons.chevronDown size={12} /> {later.length} later (after 7pm)</div>
      )}
    </div>
  );
}
